import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

const SERVICES = [
  'All Services',
  'Google Review Agent',
  'Website Development',
  'Chatbot (Carl)',
  'Automated Admin Assistant',
];

export default function MessageLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [filterService, setFilterService] = useState('All Services');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.getEmailLog()
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filtered = logs.filter(e => {
    if (filterService && filterService !== 'All Services') {
      // Try to match by subject
      const subjectKey = Object.entries(api.EMAIL_SUBJECTS || {}).find(([, v]) => v === e.subject);
      const matchedService = subjectKey ? subjectKey[0] : null;
      if (matchedService !== filterService) return false;
    }
    if (filterFrom) {
      const from = new Date(filterFrom);
      const sent = new Date(e.sentAt);
      if (sent < from) return false;
    }
    if (filterTo) {
      const to = new Date(filterTo);
      to.setHours(23, 59, 59, 999);
      const sent = new Date(e.sentAt);
      if (sent > to) return false;
    }
    if (filterStatus === 'replied' && !e.replied) return false;
    if (filterStatus === 'follow_up_sent' && !e.followUpSent) return false;
    return true;
  });

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  if (loading) return <div className="text-gray-400">Loading message log...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Message Log</h2>
        <span className="text-sm text-gray-500">{filtered.length} of {logs.length} emails</span>
      </div>

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 bg-gray-900 border border-gray-800 rounded-xl p-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">From Date</label>
          <input
            type="date"
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">To Date</label>
          <input
            type="date"
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Service</label>
          <select
            value={filterService}
            onChange={e => setFilterService(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
          >
            {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">All</option>
            <option value="replied">Replied</option>
            <option value="follow_up_sent">Follow Up Sent</option>
          </select>
        </div>
        {(filterFrom || filterTo || filterStatus || filterService !== 'All Services') && (
          <div className="flex items-end">
            <button
              onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterStatus(''); setFilterService('All Services'); }}
              className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Log table */}
      <div className="space-y-2">
        {filtered.map(e => (
          <div
            key={e.id}
            className={`bg-gray-900 border rounded-xl overflow-hidden ${e.afterHours ? 'border-indigo-500/30' : 'border-gray-800'}`}
          >
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Date/time */}
              <div className="flex-shrink-0 w-40">
                <p className="text-xs font-medium text-gray-200">{formatDateTime(e.sentAt)}</p>
                {e.afterHours && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-400">After Hours</span>
                )}
              </div>

              {/* Business info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{e.businessName}</p>
                <p className="text-xs text-gray-500 truncate">{e.to}</p>
              </div>

              {/* Subject */}
              <div className="flex-1 min-w-0 hidden md:block">
                <p className="text-xs text-gray-400 truncate">{e.subject}</p>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  e.type === 'follow-up' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {e.type === 'follow-up' ? 'Follow Up' : 'Initial'}
                </span>

                {e.replied && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">Replied</span>
                )}

                {e.followUpSent && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 border border-orange-500/20">FU Sent</span>
                )}

                <button
                  onClick={() => toggleExpand(e.id)}
                  className="text-xs px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-colors"
                >
                  {expanded[e.id] ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Expanded body */}
            {expanded[e.id] && (
              <div className="px-4 pb-4 border-t border-gray-800 pt-3">
                <p className="text-xs text-gray-400 mb-1 font-medium">Subject: {e.subject}</p>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-sans bg-gray-800/50 rounded-lg p-4 leading-relaxed">
                  {e.body}
                </pre>
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No emails match your filters.
          </div>
        )}
      </div>
    </div>
  );
}
